import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import chatSessionService, {
  type ChatSessionListParams,
  type ChatSessionSummary,
} from '../../services/api/chatSessionService';
import {
  setSessions,
  setLoading,
  setError,
  setActiveSession,
  setDrawerOpen,
  upsertSession,
  removeSession,
  updateSessionTitle,
  updateSessionPinned,
} from '../slices/chatSessionSlice';
import { setMessages, setSessionId } from '../slices/chatSlice';
import type { ChatMessage } from '../../features/chat/types/chatTypes';
import type { ContentBlock } from '../../features/chat/types/contentBlocks';

/**
 * Read the active tenant id from the tenant slice. The sessions controller
 * accepts a query-param tenant id; the backend's `IChatUserContextBuilder`
 * still validates ownership against the user's allowed memberships.
 */
function getActiveTenantId(state: RootState): number | null {
  const raw = state.tenant.activeTenant?.tenantId ?? null;
  return raw != null ? Number(raw) : null;
}

/**
 * Map a backend `ChatSessionMessage` to the in-memory `ChatMessage` shape
 * the chat panel renders. Backend returns blocks already deserialized as
 * polymorphic content-block objects (see `ChatSession_Get_Query.DeserializeBlocks`).
 */
function toChatMessage(m: {
  id: number;
  role: 'user' | 'assistant' | 'system';
  blocks: ContentBlock[];
  correlationId?: string | null;
  createdAt: string;
}): ChatMessage {
  return {
    id: String(m.id),
    role: m.role === 'system' ? 'assistant' : m.role,
    blocks: m.blocks ?? [],
    timestamp: new Date(m.createdAt).getTime(),
    correlationId: m.correlationId ?? undefined,
    feedback: null,
  };
}

export const loadSessions = createAsyncThunk(
  'chatSession/loadSessions',
  async (params: ChatSessionListParams | undefined, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));
      const state = getState() as RootState;
      const result = await chatSessionService.listSessions({
        pageNumber: params?.pageNumber ?? state.chatSession.pageNumber,
        pageSize: params?.pageSize ?? state.chatSession.pageSize,
        tenantId: params?.tenantId ?? getActiveTenantId(state),
      });
      dispatch(setSessions(result));
      return result;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load chat sessions';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const loadSessionDetail = createAsyncThunk(
  'chatSession/loadSessionDetail',
  async (id: number, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const detail = await chatSessionService.getSession(id, getActiveTenantId(state));
      if (!detail) {
        const msg = 'Session not found';
        dispatch(setError(msg));
        return rejectWithValue(msg);
      }
      // Swap the in-memory conversation to the persisted messages and update
      // the active id on both slices. Closing the drawer is the caller's
      // visual confirmation that the load succeeded.
      const messages = detail.messages.map(toChatMessage);
      dispatch(setMessages(messages));
      dispatch(setSessionId(detail.session.id));
      dispatch(setActiveSession(detail.session.id));
      dispatch(setDrawerOpen(false));
      return detail;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load chat session';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const renameSession = createAsyncThunk(
  'chatSession/renameSession',
  async (
    { id, title }: { id: number; title: string },
    { dispatch, getState, rejectWithValue }
  ) => {
    // Optimistic — paint the new title before the request returns.
    const state = getState() as RootState;
    const previousTitle = state.chatSession.sessions.find(s => s.id === id)?.title;
    dispatch(updateSessionTitle({ id, title }));
    try {
      const updated = await chatSessionService.renameSession(id, title, getActiveTenantId(state));
      // If the backend canonicalised the title (trim, length cap), reflect that.
      if (updated && updated.title !== title) {
        dispatch(updateSessionTitle({ id, title: updated.title }));
      }
      return updated;
    } catch (error: unknown) {
      // Rollback the optimistic update.
      if (previousTitle != null) dispatch(updateSessionTitle({ id, title: previousTitle }));
      const message = error instanceof Error ? error.message : 'Failed to rename chat session';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const pinSession = createAsyncThunk(
  'chatSession/pinSession',
  async (
    { id, pinned }: { id: number; pinned: boolean },
    { dispatch, getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const wasPreviouslyPinned = state.chatSession.sessions.find(s => s.id === id)?.pinned;
    dispatch(updateSessionPinned({ id, pinned }));
    try {
      const updated = await chatSessionService.pinSession(id, pinned, getActiveTenantId(state));
      return updated;
    } catch (error: unknown) {
      if (wasPreviouslyPinned != null) dispatch(updateSessionPinned({ id, pinned: wasPreviouslyPinned }));
      const message = error instanceof Error ? error.message : 'Failed to pin chat session';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const deleteSession = createAsyncThunk(
  'chatSession/deleteSession',
  async (id: number, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const snapshot: ChatSessionSummary | undefined = state.chatSession.sessions.find(s => s.id === id);
    dispatch(removeSession(id));
    try {
      await chatSessionService.deleteSession(id, getActiveTenantId(state));
      // If the deleted session was the active one, clear the in-memory
      // conversation too — otherwise the panel would still show stale
      // messages until the user starts a new chat.
      if (state.chat.sessionId === id) {
        dispatch(setMessages([]));
        dispatch(setSessionId(null));
      }
      return id;
    } catch (error: unknown) {
      // Rollback.
      if (snapshot) dispatch(upsertSession(snapshot));
      const message = error instanceof Error ? error.message : 'Failed to delete chat session';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);
